import React__default from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { setErrorDataAttribute } from '../fields/FieldError.js';
import { Box } from '../layout/Box.js';
import { Inline } from '../layout/Inline.js';
import { Stack } from '../layout/Stack.js';
import { getThemeVariantColor } from '../styles/stylePropTypes.js';
import { Text } from '../text/Text.js';
import { CHECKBOX_SIZE_REM, INPUT_READ_ONLY_DATA_ATTRIBUTE, CSSReadOnlyInput, INPUT_DISABLED_DATA_ATTRIBUTE, CSSDisabledInput, setReadOnlyDataAttribute, setDisabledDataAttribute } from './InputCommon.js';

const StyledCheckboxInput = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: ${CHECKBOX_SIZE_REM}rem;
  height: ${CHECKBOX_SIZE_REM}rem;
  width: ${CHECKBOX_SIZE_REM}rem;
  background-color: transparent;
  border: 1px solid ${COLOR_PALETTE.slateGrey40};

  &:after {
    content: '';
    position: absolute;
    border-bottom: 2px solid transparent;
    border-right: 2px solid transparent;
    height: 0.75rem;
    width: 0.4rem;
    transform: rotate(45deg);
    left: 0.45rem;
    top: 0.2rem;
  }
`;
const CheckboxLabel = styled.label`
  display: block;
  position: relative;
  cursor: pointer;
  user-select: none;
  font-weight: inherit;

  & input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  &:hover input ~ [data-hpy-web-checkbox-custom-input] {
    border-color: ${getThemeVariantColor("context")};
  }

  &:focus input ~ [data-hpy-web-checkbox-custom-input],
  & input:focus ~ [data-hpy-web-checkbox-custom-input] {
    border-color: ${getThemeVariantColor("context")};
  }

  /* value states */
  & input:checked ~ [data-hpy-web-checkbox-custom-input] {
    background-color: ${getThemeVariantColor("context")};
    border: none;
  }

  & input:checked ~ [data-hpy-web-checkbox-custom-input]::after {
    border-color: ${COLOR_PALETTE.white};
  }

  & input:checked ~ [data-hpy-web-checkbox-input-label]:after {
    display: block;
  }

  & input[data-hpy-web-checkbox-input-indeterminate]:not(:checked) ~ [data-hpy-web-checkbox-custom-input] {
    border-color: ${getThemeVariantColor("context")};
  }

  & input[data-hpy-web-checkbox-input-indeterminate]:not(:checked) ~ [data-hpy-web-checkbox-custom-input]:after {
    border-right: none;
    border-color: ${getThemeVariantColor("context")};
    transform: none;
    top: 0.1rem;
    left: 0.3125rem;
    height: 0.55rem;
    width: 0.5rem;
  }

  /* input states */
  &[${INPUT_READ_ONLY_DATA_ATTRIBUTE}] {
    ${CSSReadOnlyInput}
  }

  &[${INPUT_DISABLED_DATA_ATTRIBUTE}] {
    pointer-events: none;
  }

  &[${INPUT_DISABLED_DATA_ATTRIBUTE}] * {
    ${CSSDisabledInput}
  }

  &&[${INPUT_DISABLED_DATA_ATTRIBUTE}] input:checked ~ [data-hpy-web-checkbox-custom-input] {
    background-color: ${COLOR_PALETTE.slateGrey20};
  }

  &&[${INPUT_DISABLED_DATA_ATTRIBUTE}] input[data-hpy-web-checkbox-input-indeterminate]
    ~ [data-hpy-web-checkbox-custom-input]:after,
  &&[${INPUT_DISABLED_DATA_ATTRIBUTE}] input[data-hpy-web-checkbox-input-indeterminate]
    ~ [data-hpy-web-checkbox-custom-input] {
    border-color: ${COLOR_PALETTE.slateGrey20};
  }
`;
const CheckboxInputWrapper = styled(Box)`
  height: ${CHECKBOX_SIZE_REM}rem;
  width: ${CHECKBOX_SIZE_REM}rem;
`;
const setIndeterminateAttribute = (value, showIndeterminate)=>showIndeterminate && value === undefined && {
        "data-hpy-web-checkbox-input-indeterminate": ""
    };
/**
 * Checkbox input
 *
 * @example
 * <CheckboxInput name="chekkkers" />
 */ const CheckboxInput = ({ checked =false , className , readOnly =false , hasError =false , disabled , testId , showIndeterminate =false , onChange , label , sublabel , value , ...props })=>{
    if (!onChange) {
        readOnly = true;
    }
    return React__default.createElement(CheckboxLabel, {
        "data-hpy-web-checkbox": true,
        className: className,
        role: "checkbox",
        "aria-checked": checked,
        ...setReadOnlyDataAttribute(!!readOnly),
        ...setDisabledDataAttribute(!!disabled),
        ...setErrorDataAttribute(hasError)
    }, React__default.createElement(Inline, {
        gap: 0.5
    }, React__default.createElement(CheckboxInputWrapper, null, React__default.createElement("input", {
        type: "checkbox",
        ...props,
        ...setTestId("CheckboxInput-" + testId),
        ...setIndeterminateAttribute(value, showIndeterminate),
        readOnly: readOnly,
        "data-hpy-web-checkbox-input": true,
        checked: checked,
        disabled: disabled,
        onChange: (e)=>onChange?.(e.target?.checked)
    }), React__default.createElement(StyledCheckboxInput, {
        "data-hpy-web-checkbox-custom-input": true
    })), sublabel && React__default.createElement(Box, null, React__default.createElement(Stack, {
        gap: 0.25,
        align: "left"
    }, label && React__default.createElement(Text, {
        size: "medium",
        weight: "bold",
        "data-hpy-web-checkbox-input-label": true
    }, label), sublabel && React__default.createElement(Text, {
        size: "small"
    }, sublabel))), label && !sublabel && React__default.createElement(Inline, {
        alignY: "center"
    }, React__default.createElement(Text, {
        size: "medium",
        weight: "bold",
        "data-hpy-web-checkbox-input-label": true
    }, label))));
};
CheckboxInput.dataId = "data-hpy-web-checkbox";
CheckboxInput.displayName = "Checkbox";

export { CheckboxInput };
